import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private readonly clientId = environment.googleClientId;

  constructor() {}

  /**
   * Initialize Google Sign-In
   */
  initGoogleAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined') {
        reject('Google SDK not loaded');
        return;
      }

      google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      resolve();
    });
  }

  /**
   * Handle the credential response from Google
   */
  private handleCredentialResponse(response: any) {
    console.log('Google credential response:', response);
    // This will be handled by the component calling signInWithGoogle
  }

  /**
   * Trigger Google Sign-In popup using OAuth2 popup
   */
  signInWithGoogle(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined') {
        reject('Google SDK not loaded');
        return;
      }

      try {
        // Use Google Identity Services credential (ID token)
        google.accounts.id.initialize({
          client_id: this.clientId,
          callback: (response: any) => {
            console.log('Google credential response:', response);
            if (response.credential) {
              // This is already an ID token, perfect for our backend
              resolve(response.credential);
            } else {
              reject('No credential received from Google');
            }
          }
        });

        // Create a temporary div for the Google button and trigger it
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);

        google.accounts.id.renderButton(tempDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          width: 250
        });

        // Auto-click the button after a short delay
        setTimeout(() => {
          const button = tempDiv.querySelector('div[role="button"]') as HTMLElement;
          if (button) {
            button.click();
          } else {
            reject('Failed to create Google sign-in button');
          }
          
          // Clean up the temporary element
          setTimeout(() => {
            if (tempDiv.parentNode) {
              tempDiv.parentNode.removeChild(tempDiv);
            }
          }, 1000);
        }, 100);

      } catch (error) {
        console.error('Error initializing Google client:', error);
        reject('Failed to initialize Google authentication');
      }
    });
  }

  /**
   * Show Google sign-in popup as fallback
   */
  private showGooglePopup(): Promise<string> {
    return new Promise((resolve, reject) => {
      // Use the popup method as fallback
      google.accounts.oauth2.initTokenClient({
        client_id: this.clientId,
        scope: 'email profile',
        callback: (response: any) => {
          if (response.access_token) {
            // Get user info using the access token
            this.getUserInfoFromAccessToken(response.access_token)
              .then((userInfo: any) => {
                // Create a mock ID token structure for our backend
                // In a real implementation, you'd want to exchange this for a proper ID token
                resolve(response.access_token);
              })
              .catch(reject);
          } else {
            reject('No access token received');
          }
        },
      }).requestAccessToken();
    });
  }

  /**
   * Get user information from Google API using access token
   */
  private getUserInfoFromAccessToken(accessToken: string): Promise<any> {
    return fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to get user info from Google');
      }
      return response.json();
    });
  }

  /**
   * Create a mock ID token from user info for our backend
   */
  private createMockIdToken(userInfo: any): string {
    // Create a base64 encoded JSON that our backend can parse
    const payload = {
      iss: 'accounts.google.com',
      sub: userInfo.id,
      email: userInfo.email,
      email_verified: userInfo.verified_email,
      given_name: userInfo.given_name,
      family_name: userInfo.family_name,
      name: userInfo.name,
      picture: userInfo.picture,
      aud: this.clientId,
      exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
      iat: Math.floor(Date.now() / 1000)
    };

    // For simplicity, we'll pass the user info as a JSON string
    // In a real implementation, you'd want to get a proper ID token
    return btoa(JSON.stringify(payload));
  }

  /**
   * Alternative method using One Tap
   */
  renderGoogleButton(elementId: string): void {
    if (typeof google === 'undefined') {
      console.error('Google SDK not loaded');
      return;
    }

    google.accounts.id.renderButton(
      document.getElementById(elementId),
      {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left'
      }
    );
  }

  /**
   * Check if Google SDK is loaded
   */
  isGoogleSdkLoaded(): boolean {
    return typeof google !== 'undefined' && google.accounts;
  }

  /**
   * Simple One Tap method as fallback
   */
  signInWithGoogleOneTap(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined') {
        reject('Google SDK not loaded');
        return;
      }

      google.accounts.id.initialize({
        client_id: this.clientId,
        callback: (response: any) => {
          if (response.credential) {
            resolve(response.credential);
          } else {
            reject('No credential received from Google');
          }
        },
        auto_select: false,
        cancel_on_tap_outside: false,
      });

      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed()) {
          reject('Google One Tap is not available');
        } else if (notification.isSkippedMoment()) {
          reject('User closed Google One Tap');
        }
      });
    });
  }
}
